import { Dataset } from 'app/model/dataset/dataset';
import { BinaryDataset } from 'app/model/dataset/binary.dataset';
import { FileTypeDataset, isDatasetWithoutFileFormat } from 'app/model/dataset/file-type.dataset';
import { FormatDatasetV2 } from 'app/model/dataset/dataset.v2';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { HttpFileDataset } from 'app/model/dataset/http-file.dataset';
import { AdobeExperiencePlatformObjectDataset } from 'app/model/dataset/adobe-experience-platform.dataset';
import { stringsAreEqualCaseInsensitive } from './string.util';
import { formatTypeCopySourceTypes, formatTypeDatasetTypes, formatTypeCopySinkTypes } from 'app/registries/consts/adf-model-type-map-const';
import { Metamodel, compressionCodecType } from 'shell-models-lib';

export function isAEPDataset(dataset: Dataset): boolean {
    return dataset instanceof AdobeExperiencePlatformObjectDataset;
}

export function isBinaryTypeDataset(dataset: Dataset): boolean {
    if (dataset instanceof BinaryDataset) {
        return true;
    }
    if (isAEPDataset(dataset)) {
        return true; // we treat aep as binary dataset
    }
    if (dataset instanceof FileTypeDataset && isDatasetWithoutFileFormat(dataset)) {
        return true;
    }
    return false;
}

// used to check if a dataset is file type and specified file name
export function datasetIsFile(dataset: Dataset): boolean {
    if (dataset instanceof HttpFileDataset) {
        return true; // we treat http as file
    }
    if (isAEPDataset(dataset)) {
        return false; // we treat aep as folder
    }
    if (dataset instanceof FileTypeDataset && (dataset.fileName || FileTypeDataset.FILE_NAME.templateValue(dataset))) {
        return true;
    }
    if ((dataset instanceof FormatDatasetV2 && dataset.location && !dataset.location.isFolder())) {
        return true;
    }
    return false;
}
// used to check if a dataset is file type and specified file name
export function datasetWithZipDeflateCompression(dataset: Dataset): boolean {
    return datasetWithExpectedCompressionType(dataset, compressionCodecType.ZipDeflate, false);
}

export function datasetWithTarGZipCompression(dataset: Dataset): boolean {
    return datasetWithExpectedCompressionType(dataset, compressionCodecType.TarGZip, false);
}

export function datasetWithTarCompression(dataset: Dataset): boolean {
    return datasetWithExpectedCompressionType(dataset, compressionCodecType.tar, false);
}

export function datasetWithExpectedCompressionType(dataset: Dataset, expectedCompressiontType: string, treatNullCompressionAsTrue: boolean = true): boolean {
    if (dataset instanceof FileTypeDataset) {
        if (!dataset.compression) {
            return treatNullCompressionAsTrue ? true : false;
        }
        return (dataset.compression && stringsAreEqualCaseInsensitive(dataset.compression.compressionType, expectedCompressiontType));
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (dataset instanceof FormatDatasetV2 && stringsAreEqualCaseInsensitive((dataset as any).compressionCodec, expectedCompressiontType)) {
        return true;
    }
    return false;
}

export function isFileTypeDataset(dataset: Dataset, services: { dsSvc: DatasetRegistryService }): boolean {
    return dataset && services.dsSvc.getDatasetConfiguration(Metamodel.modelOf(dataset).name).datasetKind === 'file';
}

export function isFileTypeCategoryDataset(dataset: Dataset, services: { dsSvc: DatasetRegistryService }): boolean {
    if (dataset) {
        const datasetMetadata = services.dsSvc.getDatasetConfiguration(Metamodel.modelOf(dataset).name);
        if (datasetMetadata.category) {
            return datasetMetadata.category.includes('File');
        }
    }
    return false;
}

export function isStaticFormatTypeCopySourceOrSink(sourceSinkType: string): boolean {
    return (formatTypeCopySourceTypes.indexOf(sourceSinkType) !== -1) || (formatTypeCopySinkTypes.indexOf(sourceSinkType) !== -1);
}

export function isStaticFormatDataset(datasetType: string): boolean {
    return (formatTypeDatasetTypes.indexOf(datasetType) !== -1);
}
